// src/pages/HomePage.js
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocks } from '../hooks/useLocks';
import BottomNav from '../components/BottomNav';
import CouponToast from '../components/CouponToast';
import { useGeo } from '../hooks/useGeo';

const FILTERS = ['All', 'Sports', 'Fashion', 'Electronics', 'Beauty', 'Food'];

function FlashDealCard({ deal, onClick }) {
  const [countdown, setCountdown] = useState('');
  useEffect(() => {
    if (!deal.validUntil) return;
    const tick = () => {
      const diff = new Date(deal.validUntil) - Date.now();
      if (diff <= 0) { setCountdown(''); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(h > 0 ? `${h}h ${m}m left` : `${m}:${String(s).padStart(2, '0')} left`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deal.validUntil]);

  const distLabel = deal._distM != null
    ? (deal._distM < 1000 ? `${Math.round(deal._distM)}m away` : `${(deal._distM / 1000).toFixed(1)}km`)
    : null;

  return (
    <div className="flash-card" onClick={onClick}>
      <div className="flash-top-row">
        <span className="flash-tag">⚡ FLASH</span>
        <div className="flash-meta-row">
          {distLabel && <span className="flash-meta">{distLabel}</span>}
          {countdown && <span className="flash-countdown">{countdown}</span>}
        </div>
      </div>
      <p className="flash-store-name">{deal.store?.name}</p>
      <p className="flash-deal-title">{deal.title}</p>
      {deal.description && <p className="flash-desc">{deal.description}</p>}
      <div className="flash-divider" />
      <span className="flash-cta">Grab Deal →</span>
    </div>
  );
}

// ── Ranking helpers ────────────────────────────────────────────────────────
function scoreStore(store, visibleCoupons, catPrefs) {
  const storeCoupons = visibleCoupons.filter(c => c.storeId === store.id);
  // Best discount (normalised to 0-1)
  const bestDiscount = storeCoupons.reduce((best, c) => {
    const pct = c.discountType === 'PERCENTAGE'
      ? c.discountValue / 100
      : Math.min(c.discountValue / 3000, 0.8);
    return Math.max(best, pct);
  }, 0);
  // Freshness: newest coupon validFrom, decays to 0 over 30 days
  const newestMs = storeCoupons.reduce((t, c) => Math.max(t, new Date(c.validFrom).getTime()), 0);
  const ageDays = newestMs ? (Date.now() - newestMs) / 86400000 : 30;
  const freshness = Math.max(0, 1 - ageDays / 30);
  // Popularity: active deal count (capped at 5)
  const popularity = Math.min((store._count?.coupons || 0) / 5, 1);
  // Category affinity from user history
  const affinity = Math.min((catPrefs[store.category] || 0) / 10, 1);

  return 0.40 * bestDiscount + 0.20 * freshness + 0.15 * popularity + 0.25 * affinity;
}

function scoreProduct(product, store, coupon, catPrefs, tagPrefs) {
  const discount = coupon
    ? (coupon.discountType === 'PERCENTAGE'
        ? coupon.discountValue / 100
        : Math.min(coupon.discountValue / (product.price || 3000), 0.8))
    : 0;
  const ageDays = coupon ? (Date.now() - new Date(coupon.validFrom).getTime()) / 86400000 : 30;
  const freshness = Math.max(0, 1 - ageDays / 30);
  const popularity = Math.min((store?._count?.coupons || 0) / 5, 1);
  // Tag affinity: average score across all product tags, normalised by 10
  const tags = product.tags || [];
  const tagAffinity = tags.length
    ? Math.min(tags.reduce((sum, t) => sum + (tagPrefs[t] || 0), 0) / tags.length / 10, 1)
    : Math.min((catPrefs[store?.category] || 0) / 10, 1); // fall back to category if no tags

  return 0.40 * discount + 0.20 * freshness + 0.15 * popularity + 0.25 * tagAffinity;
}

function distanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lockCoupon, getLockStatus } = useLocks(user);

  const [catPrefs, setCatPrefs] = useState({});
  const [tagPrefs, setTagPrefs] = useState({});

  useEffect(() => {
    api.getSignals().then(({ category, tag }) => {
      setCatPrefs(category || {});
      setTagPrefs(tag || {});
    }).catch(() => {});
  }, []);

  const trackCategory = (category) => {
    if (!category) return;
    setCatPrefs(prev => ({ ...prev, [category]: Math.min((prev[category] || 0) + 1, 20) }));
    api.postSignals([{ type: 'category', key: category }]);
  };

  const trackTags = (tags) => {
    if (!tags?.length) return;
    setTagPrefs(prev => {
      const next = { ...prev };
      for (const tag of tags) next[tag] = Math.min((next[tag] || 0) + 1, 20);
      return next;
    });
    api.postSignals(tags.map(tag => ({ type: 'tag', key: tag })));
  };
  const [toastCoupons, setToastCoupons] = useState([]);
  const lastToastKeyRef = useRef('');

  const [malls, setMalls] = useState([]);
  const [selectedMall, setSelectedMall] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sho_mall_obj') || 'null'); } catch { return null; }
  });
  const [mallDropdownOpen, setMallDropdownOpen] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [mallSuggestion, setMallSuggestion] = useState(null); // { mall } — for background switch prompt
  const dropdownRef = useRef(null);
  const allMallsRef = useRef([]);

  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [locking, setLocking] = useState(null);
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const { nearbyCoupons } = useGeo(selectedMall?.id, Boolean(user && selectedMall?.id));

  // Load malls — restore saved mall immediately, only run GPS on first visit for mall selection.
  // GPS is always requested on mount separately (below) for proximity filtering.
  useEffect(() => {
    api.getMalls().then(allMalls => {
      if (!allMalls.length) return;
      allMallsRef.current = allMalls;
      setMalls(allMalls);

      const savedId = localStorage.getItem('sho_mall_id');
      const saved = savedId && allMalls.find(m => m.id === savedId);

      if (saved) {
        // Restore saved mall without changing selection — fast re-entry
        setSelectedMall(saved);
        localStorage.setItem('sho_mall_obj', JSON.stringify(saved));
      } else {
        // First visit: pick first mall immediately then refine with GPS
        setSelectedMall(allMalls[0]);
        localStorage.setItem('sho_mall_id', allMalls[0].id);
        localStorage.setItem('sho_mall_obj', JSON.stringify(allMalls[0]));
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
          ({ coords: { latitude, longitude } }) => {
            setUserLocation({ latitude, longitude });
            const sorted = [...allMalls].sort((a, b) =>
              distanceKm(latitude, longitude, a.latitude, a.longitude) -
              distanceKm(latitude, longitude, b.latitude, b.longitude)
            );
            setMalls(sorted);
            setSelectedMall(sorted[0]);
            localStorage.setItem('sho_mall_id', sorted[0].id);
            localStorage.setItem('sho_mall_obj', JSON.stringify(sorted[0]));
          },
          () => {},
          { timeout: 5000, maximumAge: 60000 }
        );
      }
    });
  }, []); // eslint-disable-line

  // Always get GPS on mount for proximity filtering — independent of mall selection
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => {
        setUserLocation({ latitude, longitude });
      },
      () => {}, // silently fail — proximity filter falls back to showing all
      { timeout: 10000, maximumAge: 30000 }
    );
  }, []); // eslint-disable-line

  // Background location check every 5 minutes — suggest mall switch if user has moved
  useEffect(() => {
    if (!navigator.geolocation) return;
    const interval = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude, longitude } }) => {
          setUserLocation({ latitude, longitude }); // consistent with { latitude, longitude }
          const allMalls = allMallsRef.current;
          if (!allMalls.length) return;
          const nearest = [...allMalls].sort((a, b) =>
            distanceKm(latitude, longitude, a.latitude, a.longitude) -
            distanceKm(latitude, longitude, b.latitude, b.longitude)
          )[0];
          const snoozedUntil = sessionStorage.getItem('sho_mall_snooze_session');
          if (!snoozedUntil || Date.now() >= parseInt(snoozedUntil, 10)) {
            setSelectedMall(current => {
              if (nearest && current && nearest.id !== current.id) {
                setMallSuggestion({ mall: nearest, lat: latitude, lng: longitude });
              }
              return current; // don't change selected mall — just suggest
            });
          }
        },
        () => {},
        { timeout: 8000, maximumAge: 0 }
      );
    }, 5 * 60 * 1000); // every 5 minutes
    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    if (!selectedMall) return;
    setLoading(true);
    setStores([]); setProducts([]); setCoupons([]);

    const load = async () => {
      const [storesResult, productsResult, couponsResult] = await Promise.allSettled([
        api.getStores(selectedMall.id),
        api.getProductsByMall(selectedMall.id),
        api.getCouponsByMall(selectedMall.id),
      ]);
      setStores(storesResult.status === 'fulfilled' ? storesResult.value : []);
      setProducts(productsResult.status === 'fulfilled' ? productsResult.value : []);
      setCoupons(couponsResult.status === 'fulfilled' ? couponsResult.value : []);
      setLoading(false);
    };
    load();
  }, [selectedMall?.id]); // eslint-disable-line

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setMallDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!nearbyCoupons?.length) return;
    const toastKey = nearbyCoupons
      .map(({ coupon }) => coupon.id)
      .sort()
      .join(',');

    if (!toastKey || toastKey === lastToastKeyRef.current) return;
    lastToastKeyRef.current = toastKey;
    setToastCoupons(nearbyCoupons);
  }, [nearbyCoupons]);

  useEffect(() => {
    lastToastKeyRef.current = '';
    setToastCoupons([]);
  }, [selectedMall?.id, user?.id]);

  const selectMall = (mall) => {
    setSelectedMall(mall);
    localStorage.setItem('sho_mall_id', mall.id);
    localStorage.setItem('sho_mall_obj', JSON.stringify(mall));
    setMallDropdownOpen(false);
    setFilter('All');
    setMallSuggestion(null);
  };

  const acceptMallSuggestion = () => {
    if (!mallSuggestion) return;
    setUserLocation({ latitude: mallSuggestion.lat, longitude: mallSuggestion.lng });
    const sorted = [...allMallsRef.current].sort((a, b) =>
      distanceKm(mallSuggestion.lat, mallSuggestion.lng, a.latitude, a.longitude) -
      distanceKm(mallSuggestion.lat, mallSuggestion.lng, b.latitude, b.longitude)
    );
    setMalls(sorted);
    selectMall(mallSuggestion.mall);
  };

  const mallDistance = (mall) => {
    if (!userLocation) return null;
    const km = distanceKm(userLocation.latitude, userLocation.longitude, mall.latitude, mall.longitude);
    return km < 1 ? `${Math.round(km * 1000)}m away` : `${km.toFixed(1)}km away`;
  };

  const handleLock = async (e, couponId) => {
    e.stopPropagation();
    if (!user) { navigate('/auth?redirect=/'); return; }
    const status = getLockStatus(couponId);
    if (status.status === 'locked' || status.status === 'cooldown') return;
    setLocking(couponId);
    try {
      await lockCoupon(couponId);
    } catch (err) {
      alert(err.message);
    } finally {
      setLocking(null);
    }
  };

  const renderLockBtn = (couponId) => {
    if (!couponId) return null;
    const status = getLockStatus(couponId);
    const isLocking = locking === couponId;

    if (isLocking) {
      return <button className="featured-lock-btn" disabled>…</button>;
    }
    if (status.status === 'locked') {
      return (
        <div className="lock-state-wrap">
          <button className="featured-lock-btn locked" disabled>✓ Locked</button>
          <span className="lock-countdown">{status.countdown}</span>
        </div>
      );
    }
    if (status.status === 'cooldown') {
      return (
        <button className="featured-lock-btn cooldown" disabled>
          🕐 {status.hoursLeft}h cooldown
        </button>
      );
    }
    return (
      <button className="featured-lock-btn" onClick={(e) => handleLock(e, couponId)}>
        {user ? '🔒 Lock Deal' : '🔒 Sign in to Lock'}
      </button>
    );
  };

  // Proximity filter: when GPS is available, only show coupons within each coupon's triggerRadiusM.
  // Coupons with triggerRadiusM >= 9999 are "always visible" — no distance check needed.
  const storeById = Object.fromEntries(stores.map(s => [s.id, s]));
  const visibleCoupons = userLocation
    ? coupons.filter(c => {
        if (c.triggerRadiusM >= 9999) return true; // always visible
        const store = storeById[c.storeId];
        if (!store) return false;
        const distM = distanceKm(userLocation.latitude, userLocation.longitude, store.latitude, store.longitude) * 1000;
        return distM <= c.triggerRadiusM;
      })
    : coupons; // no GPS — show all

  const proximityActive = Boolean(userLocation);

  // Type 3: intent-based flash deals, sorted closest-first
  const flashDeals = visibleCoupons
    .filter(c => c.discountType === 'INTENT_BASED')
    .map(c => {
      const store = storeById[c.storeId];
      const distM = userLocation && store
        ? distanceKm(userLocation.latitude, userLocation.longitude, store.latitude, store.longitude) * 1000
        : null;
      return { ...c, store, _distM: distM };
    })
    .filter(c => c.store)
    .sort((a, b) => (a._distM ?? Infinity) - (b._distM ?? Infinity))
    .slice(0, 3);

  const nonFlashCoupons = visibleCoupons.filter(c => c.discountType !== 'INTENT_BASED');

  // Type 2: product picks — scored, excludes flash coupons
  const featuredItems = products
    .filter(p => {
      if (!nonFlashCoupons.some(c => c.storeId === p.storeId)) return false;
      if (filter !== 'All') {
        const store = storeById[p.storeId];
        if (!store || store.category !== filter) return false;
      }
      return true;
    })
    .map(product => {
      const store = storeById[product.storeId];
      const coupon = nonFlashCoupons.find(c => c.storeId === product.storeId && (c.productId === product.id || !c.productId));
      return { product, store, coupon, _score: scoreProduct(product, store, coupon, catPrefs, tagPrefs) };
    })
    .sort((a, b) => b._score - a._score)
    .slice(0, 4);

  // Type 1: brand deals — enriched with headline coupon, excludes flash coupons
  const brandDeals = stores
    .filter(s => nonFlashCoupons.some(c => c.storeId === s.id))
    .filter(s => filter === 'All' || s.category === filter)
    .map(s => {
      const storeCoupons = nonFlashCoupons.filter(c => c.storeId === s.id);
      const topCoupon = storeCoupons.reduce(
        (best, c) => !best || (c.discountValue ?? 0) > (best.discountValue ?? 0) ? c : best,
        null
      );
      return { store: s, topCoupon, _score: scoreStore(s, nonFlashCoupons, catPrefs) };
    })
    .sort((a, b) => b._score - a._score)
    .slice(0, 6);
  const filteredStores = filter === 'All' ? stores : stores.filter(s => s.category === filter);

  return (
    <div className="home-root">
      <header className="home-header">
        <div className="home-header-top">
          <div className="home-brand">
            <span className="home-logo">SH</span>
            <div className="home-brand-copy">
              <span className="home-brand-name">StoreHop</span>
              <div className="mall-selector-wrap" ref={dropdownRef}>
                <button
                  className="mall-selector-btn"
                  onClick={() => malls.length > 1 && setMallDropdownOpen(o => !o)}
                  disabled={malls.length <= 1}
                >
                  <span className="mall-selector-name">{selectedMall ? selectedMall.name : 'Detecting nearest mall…'}</span>
                  {malls.length > 1 && <span className={`mall-selector-chevron ${mallDropdownOpen ? 'open' : ''}`}>›</span>}
                </button>
                {mallDropdownOpen && (
                  <div className="mall-dropdown">
                    {malls.map((mall, i) => {
                      const dist = mallDistance(mall);
                      return (
                        <button key={mall.id} className={`mall-option ${selectedMall?.id === mall.id ? 'selected' : ''}`} onClick={() => selectMall(mall)}>
                          <div className="mall-option-info">
                            <span className="mall-option-name">{mall.name}</span>
                            <span className="mall-option-addr">{mall.address}</span>
                          </div>
                          <div className="mall-option-right">
                            {dist && <span className="mall-option-dist">{dist}</span>}
                            {i === 0 && userLocation && <span className="mall-nearest-badge">Nearest</span>}
                            {selectedMall?.id === mall.id && <span className="mall-check">✓</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          <button className="profile-btn" onClick={() => navigate('/profile')}>👤</button>
        </div>

        <button className="search-trigger" onClick={() => navigate('/search')}>
          <span className="search-icon">⌕</span>
          <span className="search-placeholder">Samba, Nike store, iPhone…</span>

        </button>

        <div className="filter-row">
          {FILTERS.map(f => (
            <button key={f} className={`filter-chip ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>{f}</button>
          ))}
        </div>
      </header>

      <div className="home-content">

        {/* ── FLASH DEALS (Type 3 — INTENT_BASED) ── */}
        {flashDeals.length > 0 && (
          <section className="flash-section">
            {flashDeals.map(deal => (
              <FlashDealCard
                key={deal.id}
                deal={deal}
                onClick={() => { trackCategory(deal.store?.category); navigate(`/store/${deal.storeId}`); }}
              />
            ))}
          </section>
        )}

        {/* ── PRODUCT PICKS (Type 2) ── */}
        {(loading || featuredItems.length > 0) && (
          <section className="featured-hero-section">
            {loading ? (
              <div className="featured-hero-skeleton" />
            ) : (
              <>
                <div className="featured-hero-scroll" onScroll={e => setFeaturedIndex(Math.round(e.target.scrollLeft / e.target.offsetWidth))}>
                  {featuredItems.map(({ product, store, coupon }) => {
                    const discount = coupon
                      ? coupon.discountType === 'PERCENTAGE' ? `${coupon.discountValue}% OFF` : `₹${coupon.discountValue} OFF`
                      : null;
                    return (
                      <div key={product.id} className="featured-hero-card" onClick={() => { trackCategory(store?.category); trackTags(product.tags); navigate(`/product/${product.id}`); }}>
                        <div className="featured-hero-img" style={{ backgroundImage: product.imageUrl ? `url(${product.imageUrl})` : undefined }}>
                          {!product.imageUrl && <span className="featured-hero-placeholder">📦</span>}
                          <div className="featured-hero-gradient" />
                        </div>
                        {discount && <div className="featured-discount-badge">{discount}</div>}
                        <div className="featured-hero-info">
                          <div className="featured-hero-meta">
                            {store && <span className="featured-store-tag">{store.name}</span>}
                            <h3 className="featured-product-name">{product.name}</h3>
                            <p className="featured-product-price">₹{product.price.toLocaleString()}</p>
                          </div>
                          {coupon && renderLockBtn(coupon.id)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {featuredItems.length > 1 && (
                  <div className="featured-dots">
                    {featuredItems.map((_, i) => (
                      <span key={i} className={`featured-dot ${i === featuredIndex ? 'active' : ''}`} />
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* ── BRAND DEALS (Type 1 — store-wide) ── */}
        {(brandDeals.length > 0 || (proximityActive && nonFlashCoupons.length === 0 && coupons.length > 0)) && (
          <section className="home-section">
            <div className="section-row">
              <h2 className="section-title">Brand Deals</h2>
              {proximityActive
                ? <span className="section-sub">📍 Nearby</span>
                : <button className="see-all" onClick={() => navigate('/offers')}>See all</button>
              }
            </div>
            {proximityActive && nonFlashCoupons.length === 0 && coupons.length > 0 && (
              <p className="empty-section" style={{ margin: '12px 0' }}>No deals within range right now. Walk closer to a store!</p>
            )}
            {brandDeals.length > 0 && (
              <div className="deals-scroll">
                {brandDeals.map(({ store, topCoupon }) => {
                  const discountNum = topCoupon?.discountType === 'PERCENTAGE'
                    ? `${topCoupon.discountValue}%`
                    : topCoupon ? `₹${topCoupon.discountValue}` : null;
                  return (
                    <div key={store.id} className="brand-card" onClick={() => { trackCategory(store.category); navigate(`/store/${store.id}`); }}>
                      <div className="brand-logo-wrap">
                        {store.imageUrl
                          ? <img src={store.imageUrl} alt={store.name} className="brand-logo-img" />
                          : <span className="brand-emoji">🏪</span>}
                      </div>
                      {discountNum && (
                        <p className="brand-discount">
                          <span className="brand-discount-num">{discountNum}</span>
                          {' '}<span className="brand-discount-off">off everything</span>
                        </p>
                      )}
                      <p className="brand-name">{store.name}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* ── STORES ── */}
        <section className="home-section">
          <div className="section-row">
            <h2 className="section-title">🏬 Stores</h2>
            {selectedMall && <span className="section-sub">{filteredStores.length} in {selectedMall.name.split(' ')[0]}</span>}
          </div>
          {loading ? (
            <div className="store-skeleton-list">{[1,2,3].map(i => <div key={i} className="store-skeleton-row" />)}</div>
          ) : filteredStores.length === 0 ? (
            <div className="empty-section">No {filter !== 'All' ? filter : ''} stores found.</div>
          ) : (
            <div className="store-list-home">
              {filteredStores.map(store => (
                <div key={store.id} className="store-row-home" onClick={() => { trackCategory(store.category); navigate(`/store/${store.id}`); }}>
                  <div className="store-row-img" style={{ backgroundImage: store.imageUrl ? `url(${store.imageUrl})` : undefined }}>
                    {!store.imageUrl && <span>🏪</span>}
                  </div>
                  <div className="store-row-info">
                    <p className="store-row-name">{store.name}</p>
                    <p className="store-row-meta">{store.category} · Floor {store.floorNumber ?? '–'}</p>
                  </div>
                  {store._count?.coupons > 0 && (
                    <span className="store-row-deals">{store._count.coupons} deal{store._count.coupons > 1 ? 's' : ''}</span>
                  )}
                  <span className="store-row-arrow">›</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="home-bottom-pad" />
      </div>

      {mallSuggestion && (
        <div className="mall-switch-banner">
          <span className="mall-switch-text">
            📍 You seem to be near <strong>{mallSuggestion.mall.name}</strong>
          </span>
          <div className="mall-switch-actions">
            <button className="mall-switch-btn-accept" onClick={acceptMallSuggestion}>Switch</button>
            <button className="mall-switch-btn-dismiss" onClick={() => {
                sessionStorage.setItem('sho_mall_snooze_session', Date.now() + 4 * 60 * 60 * 1000); // 4 hours
                setMallSuggestion(null);
              }}>Stay</button>
          </div>
        </div>
      )}

      {toastCoupons.length > 0 && (
        <CouponToast coupons={toastCoupons} onClose={() => setToastCoupons([])} />
      )}

      <BottomNav active="home" />
    </div>
  );
}
